import { useCallback, useEffect, useState } from "react";
import { Button, Icon, IconButton, Input, useToast } from "@astrum/ui";
import { api, ApiError } from "../../lib/api.js";
import { useRealtimeFrames } from "../../lib/realtime-context.js";

type Bookmark = { id: string; title: string; url: string; position: number };

/** The most the server allows; one page always holds them all. */
const BOOKMARK_LIMIT = 25;

/**
 * Links pinned under the channel header. Anyone who can read the channel sees
 * them; members add, rename and remove. Only http and https are accepted
 * (the server enforces it; the form just says so before a round trip).
 */
export function BookmarkBar({ channelId, canEdit }: { channelId: string; canEdit: boolean }) {
  const { toast } = useToast();
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  /** `null` closed; `"new"` adding; otherwise the id being edited. */
  const [editing, setEditing] = useState<string | "new" | null>(null);
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api
      .get<{ bookmarks: Bookmark[] }>(`/channels/${channelId}/bookmarks?limit=${BOOKMARK_LIMIT}`)
      .then((res) => setBookmarks(res.bookmarks))
      .catch(() => setBookmarks([]));
  }, [channelId]);

  useEffect(() => {
    setBookmarks([]);
    setEditing(null);
    load();
  }, [load]);

  useRealtimeFrames((frame) => {
    if (frame.t === "bookmarks" && frame.channelId === channelId) load();
  });

  const startEdit = (bookmark: Bookmark | null) => {
    setEditing(bookmark ? bookmark.id : "new");
    setTitle(bookmark?.title ?? "");
    setUrl(bookmark?.url ?? "");
  };

  const save = () => {
    const cleanTitle = title.trim();
    const cleanUrl = url.trim();
    if (!cleanTitle || !/^https?:\/\//i.test(cleanUrl)) {
      toast("Give the link a title and a web address starting with http:// or https://.", { tone: "danger" });
      return;
    }
    setSaving(true);
    const request =
      editing === "new"
        ? api.post(`/channels/${channelId}/bookmarks`, { title: cleanTitle, url: cleanUrl })
        : api.patch(`/channels/${channelId}/bookmarks/${editing}`, { title: cleanTitle, url: cleanUrl });
    request
      .then(() => {
        setEditing(null);
        load();
      })
      .catch((error: unknown) => toast(error instanceof ApiError ? error.message : "Could not save that link.", { tone: "danger" }))
      .finally(() => setSaving(false));
  };

  const remove = (bookmark: Bookmark) => {
    api
      .delete(`/channels/${channelId}/bookmarks/${bookmark.id}`)
      .then(() => setBookmarks((prev) => prev.filter((b) => b.id !== bookmark.id)))
      .catch((error: unknown) => toast(error instanceof ApiError ? error.message : "Could not remove that link.", { tone: "danger" }));
  };

  if (bookmarks.length === 0 && !canEdit) return null;

  return (
    <div className="chat-bookmarks" aria-label="Channel bookmarks">
      {bookmarks.map((bookmark) => (
        <span key={bookmark.id} className="chat-bookmarks__item">
          <a href={bookmark.url} target="_blank" rel="noopener noreferrer" title={bookmark.url}>
            <Icon name="link" size={12} />
            {bookmark.title}
          </a>
          {canEdit && (
            <>
              <IconButton icon={<Icon name="edit" size={11} />} label={`Edit ${bookmark.title}`} size="sm" onClick={() => startEdit(bookmark)} />
              <IconButton icon={<Icon name="close" size={11} />} label={`Remove ${bookmark.title}`} size="sm" onClick={() => remove(bookmark)} />
            </>
          )}
        </span>
      ))}
      {canEdit && editing === null && bookmarks.length < BOOKMARK_LIMIT && (
        <Button size="sm" variant="ghost" icon={<Icon name="plus" size={12} />} onClick={() => startEdit(null)}>
          Add bookmark
        </Button>
      )}
      {editing !== null && (
        <form
          className="chat-bookmarks__form"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <Input aria-label="Bookmark title" placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} />
          <Input aria-label="Bookmark link" placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)} />
          <Button size="sm" type="submit" loading={saving}>
            Save
          </Button>
          <Button size="sm" variant="ghost" type="button" onClick={() => setEditing(null)}>
            Cancel
          </Button>
        </form>
      )}
    </div>
  );
}
